// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { salesRepComponentStyles, salesRepStyles } from "@gcg/App/Components/SalesRep/SalesRep.style";
import Chat from "@gcg/XDL/Icons/Chat";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Session } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import translate from "@insite/client-framework/Translate";
import Clickable from "@insite/mobius/Clickable";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import LazyImage from "@insite/mobius/LazyImage/LazyImage";
import Link from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography/Typography";
import React, { FC } from "react";
import { connect } from "react-redux";

const mapStateToProps: (state: ApplicationState) => { session: Session } = (state: ApplicationState) => {
    const session = getSession(state);
    const billTo = getBillToState(state, session?.billToId)?.value;

    return {
        session,
        billTo,
    };
};

interface SalesRepComponentProps {
    showTitle?: boolean;
    smallImageSize?: boolean;
    withBorders?: boolean;
    session?: any;
    billTo?: any;
}

type Props = ReturnType<typeof mapStateToProps> & SalesRepComponentProps;

const SalesRepComponent: FC<Props> = ({ session, showTitle, withBorders, billTo }) => {
    const secondarySalesRep =
        session.properties["secondarySalesRep"] && JSON.parse(session.properties["secondarySalesRep"]);
    const primarySalesRep = session.properties["primarySalesRep"] && JSON.parse(session.properties["primarySalesRep"]);

    if (!primarySalesRep && !secondarySalesRep) {
        return (
            <>
                {showTitle && (
                    <Typography {...salesRepComponentStyles.title}>{translate("EIS Rep Information")}</Typography>
                )}
                <GridContainer {...salesRepComponentStyles.container}>
                    <GridItem {...salesRepComponentStyles.gridItem}>
                        {siteMessage("Custom_No_Sales_Rep", "No sales rep for this account")}
                    </GridItem>
                </GridContainer>
            </>
        );
    }

    return (
        <>
            {showTitle && (
                <Typography {...salesRepComponentStyles.title}>{translate("EIS Rep Information")}</Typography>
            )}
            <GridContainer {...salesRepComponentStyles.container}>
                {primarySalesRep && (
                    <GridItem
                        {...salesRepComponentStyles.gridItem}
                        className={!withBorders && secondarySalesRep ? "addMargin" : ""}
                    >
                        <SalesRep
                            name={primarySalesRep.Name}
                            imagePath={primarySalesRep.ImagePath}
                            phoneNumber={primarySalesRep.PhoneNumber}
                            salesRepNumber={primarySalesRep.SalesRepNumber}
                            email={primarySalesRep.Email}
                            id={primarySalesRep.SalesRepNumber}
                            billTo={billTo}
                            chat={true}
                        />
                    </GridItem>
                )}

                {primarySalesRep && secondarySalesRep && withBorders && (
                    <StyledWrapper {...salesRepComponentStyles.divider} />
                )}

                {secondarySalesRep && (
                    <GridItem {...salesRepComponentStyles.gridItem}>
                        <SalesRep
                            name={secondarySalesRep.Name}
                            imagePath={secondarySalesRep.ImagePath}
                            phoneNumber={secondarySalesRep.PhoneNumber}
                            salesRepNumber={secondarySalesRep.SalesRepNumber}
                            email={secondarySalesRep.Email}
                            id={secondarySalesRep.SalesRepNumber}
                            billTo={billTo}
                            chat={true}
                        />
                    </GridItem>
                )}
            </GridContainer>
        </>
    );
};

interface SalesRepProps {
    imagePath?: string;
    name: string;
    phoneNumber?: string;
    salesRepNumber: string;
    email: string;
    id?: string;
    billTo?: any;
    chat?: boolean;
}

const SalesRep: FC<SalesRepProps> = ({ imagePath, name, phoneNumber, salesRepNumber, email, id, billTo, chat }) => {
    const handleChatLinkClick = () => {
        const customerId = billTo?.properties?.erpNumber;
        const salesOrg = billTo?.properties?.salesOrg;

        (window as any).openChat(id, customerId, salesOrg);
    };

    return (
        <StyledWrapper {...salesRepStyles.SRContainer}>
            <StyledWrapper {...salesRepStyles.SRLeftContainer}>
                <LazyImage {...salesRepStyles.SRImage} src={imagePath} />
            </StyledWrapper>
            <StyledWrapper {...salesRepStyles.SRRightContainer}>
                <Typography {...salesRepStyles.SRName} ellipsis={true}>
                    {name}
                </Typography>
                <Link {...salesRepStyles.SRPhoneNumber} href={`tel:${phoneNumber}`} target="_blank">
                    {phoneNumber}
                </Link>
                <Link {...salesRepStyles.SREmail} href={`mailto:${email}`} target="_blank">
                    {email}
                </Link>
                {chat && (
                    <Clickable {...salesRepStyles.SRChat} onClick={handleChatLinkClick}>
                        {translate("Chat with")} {name}{" "}
                        <Icon {...salesRepStyles.icon} src={Chat} size={16} color="primary" />
                    </Clickable>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(SalesRepComponent);
